import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
interface FeatureCardProps {
  title: string;
  items: string[];
  closingText?: string;
  accent?: 'default' | 'dark';
  delay?: number;
}
function FeatureCard({
  title,
  items,
  closingText,
  accent = 'default',
  delay = 0
}: FeatureCardProps) {
  const isDark = accent === 'dark';
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5,
    delay
  }} className={`rounded-xl p-6 md:p-7 shadow-lg border w-full flex flex-col ${isDark ? 'bg-[#1F3A5F] border-white/10 text-white' : 'bg-white border-gray-100 text-[#1F3A5F]'}`} data-id="element-68">
      <h3 className={`text-lg md:text-xl font-bold mb-5 font-oswald leading-tight ${isDark ? 'text-[#D4A574]' : 'text-[#1F3A5F]'}`} data-id="element-69">
        {title}
      </h3>
      <ul className="space-y-3 mb-5 flex-grow" data-id="element-70">
        {items.map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-71">
            <CheckCircle2 className={`flex-shrink-0 mt-0.5 ${isDark ? 'text-[#D4A574]' : 'text-[#2D6A4F]'}`} size={18} data-id="element-72" />
            <span className={`text-[15px] ${isDark ? 'text-gray-100' : 'text-gray-700'}`} data-id="element-73">
              {item}
            </span>
          </li>)}
      </ul>
      {closingText && <div className={`pt-4 border-t ${isDark ? 'border-white/15' : 'border-gray-100'}`} data-id="element-74">
          <p className={`text-base md:text-lg font-bold font-playfair italic ${isDark ? 'text-white' : 'text-[#1F3A5F]'}`} data-id="element-75">
            {closingText}
          </p>
        </div>}
    </motion.div>;
}
export function Features() {
  return <>
      {/* DIAPOSITIVAS */}
      <section className="py-16 md:py-20 bg-white" data-id="element-76">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-77">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-78">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 text-[#1F3A5F] font-oswald uppercase tracking-wide" data-id="element-79">
              🖥 DIAPOSITIVAS LISTAS PARA USAR EN CLASE
            </h2>
            <p className="text-lg md:text-xl text-center text-[#D4A574] font-playfair italic font-semibold mb-10" data-id="element-80">
              Uno de los puntos más fuertes del sistema.
            </p>

            <div className="grid md:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto" data-id="element-81">
              <FeatureCard title="Archivos completos de diapositivas" items={['modernas y visuales', 'organizadas por clase', 'con imágenes y cuadros', 'ejemplos y esquemas', 'mapas conceptuales', 'explicaciones claras']} closingText="Todo listo para proyectar." delay={0} data-id="element-82" />
              <FeatureCard title="Diseñadas para tu aula" items={['reemplazar el pizarrón', 'ahorrar tiempo', 'mantener la atención del curso', 'explicar más rápido y mejor']} closingText="Clases ágiles y profesionales." accent="dark" delay={0.1} data-id="element-83" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ACTIVIDADES */}
      <section className="py-16 md:py-20 bg-[#FAF7F2]" data-id="element-84">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-85">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-86">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 text-[#1F3A5F] font-oswald uppercase tracking-wide" data-id="element-87">
              🎮 ACTIVIDADES QUE HACEN PARTICIPAR AL CURSO
            </h2>
            <div className="text-center mb-10" data-id="element-88">
              <p className="text-lg text-gray-600" data-id="element-89">Se terminó la rutina:</p>
              <p className="text-xl md:text-2xl font-bold text-[#1F3A5F] font-playfair italic" data-id="element-90">
                "leer, copiar y estudiar".
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto" data-id="element-91">
              <FeatureCard title="El sistema incluye" items={['actividades dinámicas', 'juegos pedagógicos', 'desafíos interactivos', 'propuestas creativas', 'ejercicios modernos y entretenidos']} closingText="Variedad real para cada clase." delay={0} data-id="element-92" />
              <FeatureCard title="Pensadas para" items={['mejorar la comprensión', 'aumentar la participación', 'hacer clases mucho más dinámicas']} closingText="Alumnos activos, no pasivos." accent="dark" delay={0.1} data-id="element-93" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* EVALUACIONES Y TRABAJOS PRÁCTICOS */}
      <section className="py-16 md:py-20 bg-white" data-id="element-94">
        <div className="container mx-auto px-4 max-w-6xl" data-id="element-95">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-96">
            <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 text-[#1F3A5F] font-oswald uppercase tracking-wide" data-id="element-97">
              🧪 EVALUACIONES Y TRABAJOS PRÁCTICOS LISTOS
            </h2>
            <p className="text-lg md:text-xl text-center mb-10 text-gray-600 font-playfair italic" data-id="element-98">
              No más horas creando consignas desde cero.
            </p>

            <div className="grid md:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto" data-id="element-99">
              <FeatureCard title="Evaluaciones completas" items={['consignas claras y listas para usar', 'estructura profesional', 'formato uniforme', 'listo para imprimir o digital']} closingText="Imprimís y entregás." delay={0} data-id="element-100" />
              <FeatureCard title="Trabajos prácticos desarrollados" items={['propuestas pedagógicas pensadas', 'consignas integradoras', 'aplicables a cualquier curso', 'fáciles de adaptar']} closingText="Cero horas armando desde cero." accent="dark" delay={0.1} data-id="element-101" />
            </div>
          </motion.div>
        </div>
      </section>
    </>;
}